import { ReasoningStepResultType, ReasoningStepStatus, ReasoningStepType, } from "@f2e/shared/types";
export const historyApiToHistory = (item) => ({
    id: item.id,
    name: item.name,
    createdAt: item.created_at ?? "",
    updatedAt: item.updated_at ?? "",
    totalCostCredits: item.total_cost_credits ?? 0,
    snippet: item.snippet,
    workspace: item.workspace
        ? {
            id: item.workspace.id,
            name: item.workspace.name,
            default: item.workspace.default ?? false,
            owner: item.workspace.owner,
            isJoined: item.workspace.is_joined,
            myRole: item.workspace.my_role,
            myPermissions: new Set(item.workspace.my_permissions ?? []),
            createdAt: item.workspace.created_at ?? "",
            updatedAt: item.workspace.updated_at ?? "",
            members: item.workspace.members,
            organizationId: item.workspace.organization_id,
            brand: item.workspace.brand,
            apiKeyClientsBypassWhitelist: item.workspace.api_key_clients_bypass_whitelist,
        }
        : { id: "", name: "", default: false, createdAt: "", updatedAt: "" },
    externalUserId: item.external_user_id,
    canReply: item.can_reply,
    replyDisabledReason: item.reply_disabled_reason ?? undefined,
    clientUserId: item.client_user_id ?? undefined,
    creator: item.creator,
    agentId: item.agent_id,
    agentName: item.agent_name,
    feedbackConversationCounts: item.feedback_conversation_counts,
});
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
export const stripLegacyToolTags = (content) => content.replace(/<tool\b[^>]*>[\s\S]*?<\/tool>\s*/gi, "").trimStart();
const stringifyPartValue = (value) => {
    if (typeof value === "string")
        return value;
    if (value == null)
        return "";
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    }
    catch {
        return String(value);
    }
};
const toolNameToReasoningStepType = (toolName) => {
    const normalized = toolName.replace(/^consultant_/, "");
    const knownTypes = {
        search_web: ReasoningStepType.ConsultantSearchWeb,
        fetch_web_content: ReasoningStepType.ConsultantFetchWebContent,
        retrieve_context_objs: ReasoningStepType.ConsultantRetrieveContextObjs,
        get_context_obj_lines: ReasoningStepType.ConsultantGetContextObjLines,
        list_kb_files: ReasoningStepType.ConsultantListKbFiles,
        call_agent: ReasoningStepType.ConsultantCallAgent,
        memory: ReasoningStepType.ConsultantMemory,
        generate_image: ReasoningStepType.ConsultantGenerateImage,
        http_request: ReasoningStepType.ConsultantHttpRequest,
        tabular_query: ReasoningStepType.ConsultantTabularQuery,
        analyze_histories: ReasoningStepType.ConsultantAnalyzeHistories,
        lookup: ReasoningStepType.ConsultantLookup,
        lookup_agent: ReasoningStepType.ConsultantLookupAgent,
        request_form: ReasoningStepType.ConsultantRequestForm,
        finished: ReasoningStepType.ConsultantFinished,
    };
    return (knownTypes[toolName] ??
        knownTypes[normalized] ??
        `consultant_${normalized}`);
};
const getToolName = (part) => {
    const toolName = part.content.tool_name;
    if (typeof toolName === "string" && toolName.trim()) {
        return toolName.trim();
    }
    const reasoningStepType = part.metadata.reasoning_step_type;
    if (typeof reasoningStepType === "string" && reasoningStepType.trim()) {
        return reasoningStepType.replace(/^consultant_/, "");
    }
    return "tool";
};
export const getResumableHistoryAnalysisToolCalls = (parts) => {
    const returnedToolCalls = new Set(parts
        .filter((part) => part.part_kind === "tool-return" &&
        getToolName(part) === "analyze_histories")
        .map((part) => `${part.conversation_group_id}:${String(part.content.tool_call_id ?? "")}`));
    const resolvedToolCalls = new Set(parts.flatMap((part) => {
        const analysis = part.metadata.history_analysis;
        if (!isRecord(analysis))
            return [];
        return [
            analysis.resumed_from_tool_call_id,
            analysis.superseded_tool_call_id,
        ].filter((toolCallId) => typeof toolCallId === "string");
    }));
    return parts.filter((part) => {
        const analysis = part.metadata.history_analysis;
        const toolCallId = part.content.tool_call_id;
        return (part.part_kind === "tool-call" &&
            getToolName(part) === "analyze_histories" &&
            typeof toolCallId === "string" &&
            !resolvedToolCalls.has(toolCallId) &&
            !returnedToolCalls.has(`${part.conversation_group_id}:${toolCallId}`) &&
            isRecord(analysis) &&
            analysis.status !== "cancelled" &&
            typeof analysis.run_id === "string");
    });
};
const getToolCallId = (part) => typeof part.content.tool_call_id === "string" &&
    part.content.tool_call_id.trim()
    ? part.content.tool_call_id
    : `part-${part.id ?? part.sequence}`;
const getToolTimestamp = (part) => {
    const timestamp = part.content.timestamp;
    if (typeof timestamp === "string" && timestamp)
        return timestamp;
    return part.created_at ?? undefined;
};
const getReasoningStepType = (part) => {
    const reasoningStepType = part.metadata.reasoning_step_type;
    if (typeof reasoningStepType === "string" && reasoningStepType.trim()) {
        return reasoningStepType;
    }
    return toolNameToReasoningStepType(getToolName(part));
};
const getToolArgs = (part) => {
    const args = isRecord(part.content.args) ? part.content.args : undefined;
    const historyAnalysis = part.metadata.history_analysis;
    if (getToolName(part) !== "analyze_histories" || !isRecord(historyAnalysis)) {
        return args;
    }
    return { ...(args ?? {}), history_analysis: historyAnalysis };
};
const getToolResult = (part) => {
    const result = part.content.content;
    if (isRecord(result))
        return result;
    if (result == null || result === "")
        return undefined;
    return {
        type: ReasoningStepResultType.Json,
        data: result,
    };
};
export const conversationPartToReasoningStep = (part) => {
    if (part.part_kind !== "tool-call" && part.part_kind !== "tool-return") {
        return undefined;
    }
    const toolName = getToolName(part);
    const timestamp = getToolTimestamp(part);
    const isReturn = part.part_kind === "tool-return";
    const outcome = typeof part.content.outcome === "string" ? part.content.outcome : undefined;
    const failed = isReturn && outcome != null && outcome !== "success";
    const result = isReturn ? getToolResult(part) : undefined;
    return {
        id: getToolCallId(part),
        type: getReasoningStepType(part),
        content: typeof part.content.content === "string" && part.content.content.trim()
            ? part.content.content
            : toolName,
        status: isReturn
            ? failed
                ? ReasoningStepStatus.Failed
                : ReasoningStepStatus.Completed
            : ReasoningStepStatus.Running,
        args: getToolArgs(part),
        result,
        startTime: !isReturn ? timestamp : undefined,
        endTime: isReturn ? timestamp : undefined,
        error: failed ? stringifyPartValue(part.content.content) : undefined,
    };
};
const buildUrl = (baseUrl, path, query) => {
    const search = query?.toString();
    return `${baseUrl}${path}${search ? `?${search}` : ""}`;
};
const UNAUTHORIZED_ERROR_MESSAGE = "Unauthorized";
const createCodeerHttpError = (message, status, response, data) => {
    const error = new Error(message);
    error.status = status;
    error.response = response;
    error.data = data;
    return error;
};
const apiRequest = async (getBaseUrl, getAuthHeaders, onUnauthorized, path, init) => {
    if (!path) {
        throw new Error("Path is required");
    }
    const authHeaders = await getAuthHeaders?.();
    const multipart = init?.body instanceof FormData;
    const response = await fetch(buildUrl(getBaseUrl(), path, init?.searchParams), {
        method: init?.method ?? "GET",
        headers: {
            ...(multipart ? {} : { "Content-Type": "application/json" }),
            ...(authHeaders ?? {}),
        },
        credentials: "include",
        body: multipart
            ? init.body
            : init?.body
                ? JSON.stringify(init.body)
                : undefined,
    });
    if (!response.ok) {
        if (response.status === 401) {
            onUnauthorized?.();
            throw new Error(UNAUTHORIZED_ERROR_MESSAGE);
        }
        const responseText = await response
            .clone()
            .text()
            .catch(() => "");
        let responseData;
        try {
            responseData = responseText ? JSON.parse(responseText) : undefined;
        }
        catch {
            responseData = undefined;
        }
        const message = isRecord(responseData) && typeof responseData.message === "string"
            ? responseData.message
            : responseText;
        throw createCodeerHttpError(message || `Request failed (${response.status}) for ${path}`, response.status, response, responseData);
    }
    return (await response.json());
};
export const createCodeerV2ApiClient = ({ getBaseUrl, getAuthHeaders, onUnauthorized, conversationLimit = 301, messageWindow = "earliest", }) => {
    const fetchPartsData = async (chatId) => {
        if (!chatId || chatId <= 0) {
            return { chat_id: chatId, messages: [], reply_context_anchor: null };
        }
        const searchParams = new URLSearchParams({
            limit: String(conversationLimit),
            offset: "0",
        });
        if (messageWindow === "latest") {
            searchParams.set("window", "latest");
        }
        const resp = await apiRequest(getBaseUrl, getAuthHeaders, onUnauthorized, `/chats/${chatId}/messages`, { searchParams });
        return (resp.data ?? {
            chat_id: chatId,
            messages: [],
            reply_context_anchor: null,
        });
    };
    const fetchParts = async (chatId) => (await fetchPartsData(chatId)).messages;
    const updateChat = async (chatId, name) => {
        const resp = await apiRequest(getBaseUrl, getAuthHeaders, onUnauthorized, `/chats/${chatId}`, {
            method: "PUT",
            body: { name },
        });
        return resp.data ? historyApiToHistory(resp.data) : undefined;
    };
    const createPartFeedback = async ({ chatId, partId, legacyConversationId, type, content, }) => {
        const hasPartId = typeof partId === "number" && Number.isFinite(partId) && partId > 0;
        const targetPartId = hasPartId ? partId : 0;
        const resp = await apiRequest(getBaseUrl, getAuthHeaders, onUnauthorized, `/chats/${chatId}/parts/${targetPartId}/feedbacks`, {
            method: "POST",
            body: {
                type,
                content: content || undefined,
                legacy_conversation_id: hasPartId
                    ? undefined
                    : legacyConversationId || undefined,
            },
        });
        return (resp.error_code ?? 0) === 0;
    };
    const submitFormRequest = async ({ chatId, formRequestId, conversationGroupId, submission, rejected, }) => {
        const uploadedSubmission = { ...submission };
        if (!rejected) {
            // Upload only on submit, so removing a selection never creates a stored file.
            for (const [key, value] of Object.entries(uploadedSubmission)) {
                if (!(value instanceof File))
                    continue;
                const body = new FormData();
                body.append("file", value);
                const uploaded = await apiRequest(getBaseUrl, getAuthHeaders, onUnauthorized, `/chats/${chatId}/form-requests/${formRequestId}/files`, {
                    method: "POST",
                    body,
                    searchParams: new URLSearchParams({ field_key: key }),
                });
                if (!uploaded.data || uploaded.error_code)
                    throw new Error(uploaded.message || "File upload failed");
                uploadedSubmission[key] = uploaded.data;
            }
        }
        const resp = await apiRequest(getBaseUrl, getAuthHeaders, onUnauthorized, `/chats/${chatId}/form-requests/${formRequestId}/submit`, {
            method: "POST",
            body: {
                conversation_group_id: conversationGroupId,
                rejected: rejected || undefined,
                submission: rejected ? undefined : uploadedSubmission,
            },
        });
        return (resp.error_code ?? 0) === 0;
    };
    return {
        fetchPartsData,
        fetchParts,
        updateChat,
        createPartFeedback,
        submitFormRequest,
    };
};
//# sourceMappingURL=codeer.js.map